import { z, ZodObject } from "zod"

import {
  CertificateCategorySchema,
  CertificateCategoryType,
  CertificateStatusEnum,
} from "@tfg/types/schemas/enum"

import { formFieldType } from "~/pages/Admin/components/TypesForm.tsx"

export const typeObject = z.object({
  name: z.string().nullable(),
})

export const baseTemplate = z.object({
  name: z.string().nullable(),
  reference: z.string().nullable(),
  issueDate: z.coerce.date().nullable(),
  type: typeObject.nullable(),
  status: CertificateStatusEnum,
  certificationBody: z.string().nullable(),
  certifiedCompanyName: z.string().nullable(),
  certifiedCompanyAddress: z.string().nullable(),
  certifiedCompanyLicenseNumber: z.string().nullable(),
  rejectionMessage: z.string().nullable(),
})

export const scopeCertificateTemplate = baseTemplate
  .extend({
    productCategories: z.string().array().nullable(),
    productDetails: z.string().array().nullable(),
    processingSteps: z.string().array().nullable(),
    expiryDate: z.coerce.date().nullable(),
  })

  .partial()

export const transactionCertificateTemplate = baseTemplate
  .extend({
    productCategories: z.string().array().nullable(),
    buyerOfCertifiedProducts: z.string().nullable(),
    certifiedWeight: z.string().nullable(),
    linkedScopeCertificateNumber: z.string().nullable(),
  })
  .partial()

export const otherInitiativeCertificateTemplate = baseTemplate
  .extend({
    processingSteps: z.string().array().nullable(),
    expiryDate: z.coerce.date().nullable(),
    score: z.string().nullable(),
  })
  .partial()

export const excludedFieldsForForm = [
  "issueDate",
  "status",
  "type",
  "rejectionMessage",
]
export const omitFieldsForForm = excludedFieldsForForm.reduce(
  (acc, field) => {
    acc[field] = true
    return acc
  },
  {} as Record<string, true>
)

export type CertificateTemplateType = {
  [key in CertificateCategoryType]: z.ZodObject<any>
}

export const certificateTemplates: CertificateTemplateType = {
  [CertificateCategorySchema.enum.SCOPE]: scopeCertificateTemplate,
  [CertificateCategorySchema.enum.TRANSACTION]: transactionCertificateTemplate,
  [CertificateCategorySchema.enum.OTHER_INITIATIVES]:
    otherInitiativeCertificateTemplate,
  [CertificateCategorySchema.enum.UNKNOWN]: scopeCertificateTemplate,
}

export type ScopeCertificate = z.infer<typeof scopeCertificateTemplate>
export type TransactionCertificate = z.infer<
  typeof transactionCertificateTemplate
>
export type OtherInitiativeCertificate = z.infer<
  typeof otherInitiativeCertificateTemplate
>

export type FilteredCertificate =
  | ScopeCertificate
  | TransactionCertificate
  | OtherInitiativeCertificate

export function getTemplateKeysForForm(
  template: ZodObject<any>,
  certificate: FilteredCertificate
): Array<keyof FilteredCertificate> | null {
  const result = template.omit(omitFieldsForForm).safeParse(certificate)
  if (result.success) {
    return Object.keys(result.data) as Array<keyof FilteredCertificate>
  } else {
    return null
  }
}
export function getCertificateTemplate(
  category: CertificateCategoryType
): ZodObject<any> {
  return certificateTemplates[category]
}

export function filterCertificatesWithTemplate(
  certificate: FilteredCertificate,
  template: ZodObject<any>,
  form?: boolean
): FilteredCertificate | null {
  if (form) {
    template = template.omit(omitFieldsForForm)
  }
  const parseResult = template.safeParse(certificate)
  if (parseResult.success) {
    return parseResult.data
  } else {
    console.error("Error parsing certificate", parseResult.error)
    return null
  }
}

export function getCertificateFormFieldType(type: string): formFieldType {
  switch (type) {
    case "string":
      return "text"
    case "array":
      return "tagsInput"
    default:
      return "text"
  }
}

export function getTypeOfKey(
  key: keyof FilteredCertificate,
  certificate: FilteredCertificate
) {
  if (Array.isArray(certificate[key])) {
    return "array"
  } else {
    return "string"
  }
}
